// The nominated representative of the client application (SEP-293.8).
//
// THE SOURCE IS THE FIRM'S OWN FORM, in two variants:
//
//   - `DGW N/L Rep Form (Disc/Adv) v1.1 0126`, the "Nominated & Legal
//     Representative Appointment Form" for the Discretionary Managed service
//     and the Advisory service.
//   - `DGW N/L Rep Form (XO) v1.2 0626`, the same form for the Execution Only
//     service.
//
// The online form asks the questions of the paper form at the end of Part 10
// (Third party disclosure). A yes or no question opens them. The service type
// of the application selects the variant:
//
//   - Both variants ask the personal information, the addresses, the contact
//     details, the health questions and the declarations.
//   - Only the Discretionary and Advisory variant asks the knowledge and
//     experience of the representative and the risk questions.
//   - Only the Execution Only variant asks about recent changes or events.
//
// # WHAT THE ONLINE FORM DOES NOT ASK
//
//   - Section 1 of the paper form (the personal information of the client):
//     the client already gave it in Part 1.
//   - The CLASSIFICATION section: Part 7 of the application already holds the
//     same text about the client classification.
//   - The signature of the representative: a representative cannot sign
//     online. The client accepts the declarations with a tick, and the
//     explainer of the tick tells the client that the firm gets the signature
//     of the representative off-line.
//
// # THE WORDS
//
// Each label, option and paragraph is the text of the paper form, with these
// changes:
//
//   - The Discretionary and Advisory variant prints "How secure are the
//     Appliant's sources of income?". The online form corrects the typo to
//     "Applicant's".
//   - The paper form prints "Have you ever taken place in a placing or a new
//     issue". Part 7 of the application already corrects it to "taken part",
//     and this block does the same.
//   - The paper form lists the five types of representative under two
//     headings. Each option label here names its heading, because a list of
//     options has no headings.
//
// # THE ANSWERS ARE ABOUT THE REPRESENTATIVE
//
// No answer here is an answer about the client, so no answer prefills a fact
// find (`CLIENT_APPLICATION_FACT_FIND_UNMAPPED`). The investment manager reads
// each answer in the staff review of Part 10. The submission makes no
// relationship row: the investment manager makes it.

import type {
    ClientApplicationCondition,
    ClientApplicationQuestion,
} from './client-application-questions';

/** The yes or no question that opens the representative block. */
export const NOMINATED_REPRESENTATIVE_GATE_ID = 'appoint_nominated_representative';

/** The service types of the Discretionary and Advisory variant, as the bank labels them. */
export const NOMINATED_REPRESENTATIVE_DISCRETIONARY_AND_ADVISORY_SERVICES = [
    'Discretionary Managed service',
    'Advisory service',
] as const;

/** The service type of the Execution Only variant, as the bank labels it. */
export const NOMINATED_REPRESENTATIVE_EXECUTION_ONLY_SERVICES = ['Execution Only service'] as const;

const APPOINTED: ClientApplicationCondition = {
    questionId: NOMINATED_REPRESENTATIVE_GATE_ID,
    equals: ['Yes'],
};

const DISCRETIONARY_OR_ADVISORY: ClientApplicationCondition = {
    questionId: 'service_type',
    equals: NOMINATED_REPRESENTATIVE_DISCRETIONARY_AND_ADVISORY_SERVICES,
};

const EXECUTION_ONLY: ClientApplicationCondition = {
    questionId: 'service_type',
    equals: NOMINATED_REPRESENTATIVE_EXECUTION_ONLY_SERVICES,
};

/** Both conditions of the Discretionary and Advisory variant. */
const APPOINTED_DISCRETIONARY_OR_ADVISORY = [APPOINTED, DISCRETIONARY_OR_ADVISORY] as const;

/** Both conditions of the Execution Only variant. */
const APPOINTED_EXECUTION_ONLY = [APPOINTED, EXECUTION_ONLY] as const;

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Other'] as const;

const AGREEMENT_SCALE = [
    'Strongly disagree',
    'Disagree',
    'Neither agree nor disagree',
    'Agree',
    'Strongly agree',
] as const;

const TEN_YEAR_CHANGE_SCALE = [
    'Decrease significantly',
    'Decrease somewhat',
    'Remain stable',
    'Increase somewhat',
    'Increase significantly',
] as const;

/** The five types of representative, with the heading of the paper form in each label. */
export const NOMINATED_REPRESENTATIVE_TYPE_OPTIONS = [
    'Nominated Representative - Family arrangements',
    'Nominated Representative - Minors',
    'Legal Representative - Enduring Power of Attorney (EPA)',
    'Legal Representative - Lasting Power of Attorney (LPA)',
    'Legal Representative - Deputy Court Order (DCO)',
] as const;

const PRODUCT_ROWS: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'cash_deposits', label: 'Cash/Deposits' },
    { key: 'fixed_income', label: 'Fixed Income' },
    { key: 'equities', label: 'Equities' },
    { key: 'funds', label: 'Funds' },
    { key: 'alternatives', label: 'Alternatives' },
    { key: 'derivatives', label: 'Derivatives' },
];

const GROUP = {
    appointment: 'Nominated & Legal Representative',
    personal: 'Representative - personal information',
    currentAddress: 'Representative - current residential address',
    previousAddress: 'Representative - previous residential address',
    contact: 'Representative - contact details',
    health: 'Representative - health',
    knowledge: 'Representative - knowledge & experience',
    risk: 'Risk tolerance & capacity for loss',
    signatures: 'Signatures',
} as const;

/** The id of one representative question. */
function rep(key: string): string {
    return `nominated_representative_${key}`;
}

/** The address lookup of the current address of the representative. */
const CURRENT_ADDRESS_TARGETS = {
    houseNoName: rep('address_house_no_name'),
    street: rep('address_street'),
    townCity: rep('address_town_city'),
    county: rep('address_county'),
    postcode: rep('address_postcode'),
    country: rep('address_country'),
} as const;

/** The address lookup of the previous address of the representative. */
const PREVIOUS_ADDRESS_TARGETS = {
    houseNoName: rep('previous_address_house_no_name'),
    street: rep('previous_address_street'),
    townCity: rep('previous_address_town_city'),
    county: rep('previous_address_county'),
    postcode: rep('previous_address_postcode'),
    country: rep('previous_address_country'),
} as const;

/** The six fields of one address. The house and the town, postcode and country are required when `required` is true. */
function addressQuestions(
    prefix: string,
    group: string,
    targets: typeof CURRENT_ADDRESS_TARGETS | typeof PREVIOUS_ADDRESS_TARGETS,
    required: boolean,
    explainer?: string,
): ClientApplicationQuestion[] {
    const need = required ? ({ required: true } as const) : {};
    return [
        {
            id: rep(`${prefix}_house_no_name`),
            kind: 'short_text',
            label: 'House no./name',
            ...need,
            group,
            showWhen: APPOINTED,
            addressLookup: targets,
            ...(explainer ? { explainer } : {}),
            excludeFromPrefill: true,
        },
        {
            id: rep(`${prefix}_street`),
            kind: 'short_text',
            label: 'Street',
            group,
            showWhen: APPOINTED,
            excludeFromPrefill: true,
        },
        {
            id: rep(`${prefix}_town_city`),
            kind: 'short_text',
            label: 'Town/City',
            ...need,
            group,
            showWhen: APPOINTED,
            excludeFromPrefill: true,
        },
        {
            id: rep(`${prefix}_county`),
            kind: 'short_text',
            label: 'County',
            group,
            showWhen: APPOINTED,
            excludeFromPrefill: true,
        },
        {
            id: rep(`${prefix}_postcode`),
            kind: 'short_text',
            label: 'Postcode',
            textFormat: 'postcode',
            ...need,
            group,
            showWhen: APPOINTED,
            excludeFromPrefill: true,
        },
        {
            id: rep(`${prefix}_country`),
            kind: 'country_select',
            label: 'Country',
            ...need,
            group,
            showWhen: APPOINTED,
            excludeFromPrefill: true,
        },
    ];
}

/** One risk question of the Discretionary and Advisory variant. Each one is required. */
function riskQuestion(
    key: string,
    label: string,
    options: readonly string[],
    explainer?: string,
): ClientApplicationQuestion {
    return {
        id: rep(`risk_${key}`),
        kind: 'single_select',
        label,
        ...(explainer ? { explainer } : {}),
        options: [...options],
        required: true,
        group: GROUP.risk,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    };
}

/** The questions of the representative block, before the version. */
const QUESTIONS: readonly ClientApplicationQuestion[] = [
    {
        id: NOMINATED_REPRESENTATIVE_GATE_ID,
        kind: 'yes_no',
        label: 'Would you like to appoint a nominated representative for this account?',
        // SEP-340 item 19. The words of the REPRESENTATIVES box on page 1 of
        // the paper form.
        explainer:
            'Nominated Representative (NR): this type of representative is chosen by the account holder to make investment decisions on their behalf in certain situations. These include family arrangements, where a third party handles the investment decisions for the account holder(s), and minors, where a parent, grandparent, or guardian makes decisions for someone under 18.',
        explainerParagraphs: [
            {
                text: 'If a representative is involved, you and they will need to complete a separate form to cover these arrangements.',
            },
        ],
        required: true,
        group: GROUP.appointment,
        excludeFromPrefill: true,
    },
    {
        id: rep('types'),
        kind: 'multi_select',
        label: 'Please select which representatives apply to this application.',
        explainer:
            "Before completing this form, it's important to understand the different types of representatives and what they do, because each has a specific role when it comes to managing investments for someone else.",
        explainerParagraphs: [
            {
                text: 'Nominated Representative (NR): this type of representative is chosen by the account holder to make investment decisions on their behalf in certain situations.',
            },
            {
                text: 'Legal Representative (LR): is appointed through formal legal documents. We will need to see the original or a certified copy of these legal documents.',
            },
            {
                text: 'The representative (either nominated or legal) is the individual whom we assess with regards to knowledge and experience relating to investing. They will also be responsible for receiving communications and recommendations from us. We assess their investment knowledge and experience to determine the best approach and structure for the investment portfolio.',
                showWhen: DISCRETIONARY_OR_ADVISORY,
            },
            {
                text: 'The representative (whether nominated or acting under a legal authority) is authorised to provide instructions in relation to your investments. This may include, but is not limited to, placing transactions, participating in corporate actions, and requesting payments to your nominated bank account.',
                showWhen: EXECUTION_ONLY,
            },
            {
                text: 'The representative may also receive communications relating to your investments, in accordance with your preferences.',
                showWhen: EXECUTION_ONLY,
            },
            {
                text: 'This authority will remain in place until you notify us otherwise. Where you act through a representative, or where an account is held jointly or by an organisation, we may be required to assess the appropriateness of transactions instructed by your representative. In this circumstance, we will assess your representative’s knowledge and experience as well as your own in order to determine appropriateness. We will not accept instructions we consider inappropriate for you personally, and in line with regulatory requirements, we reserve the right to decline to act on any instruction we reasonably consider to be inappropriate.',
                showWhen: EXECUTION_ONLY,
            },
        ],
        options: [...NOMINATED_REPRESENTATIVE_TYPE_OPTIONS],
        optionNotes: {
            'Nominated Representative - Family arrangements':
                'A third party handles the investment decisions for the account holder(s).',
            'Nominated Representative - Minors':
                'A parent, grandparent, or guardian makes decisions for someone under 18.',
        },
        required: true,
        group: GROUP.appointment,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },

    // 2. REPRESENTATIVE - PERSONAL INFORMATION
    {
        id: rep('title'),
        kind: 'single_select',
        label: 'Title',
        options: [...TITLE_OPTIONS],
        required: true,
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('title_other'),
        kind: 'short_text',
        label: 'Other (please specify)',
        group: GROUP.personal,
        showWhen: { questionId: rep('title'), equals: ['Other'] },
        excludeFromPrefill: true,
    },
    {
        id: rep('surname'),
        kind: 'short_text',
        label: 'Surname',
        required: true,
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('forenames'),
        kind: 'short_text',
        label: 'Forename(s)',
        required: true,
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('date_of_birth'),
        kind: 'date',
        label: 'Date of Birth',
        required: true,
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('primary_tax_residency'),
        kind: 'country_select',
        label: 'Primary Tax Residency',
        required: true,
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('tax_identifier'),
        kind: 'short_text',
        label: 'Tax Identifier',
        group: GROUP.personal,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    ...addressQuestions('address', GROUP.currentAddress, CURRENT_ADDRESS_TARGETS, true),
    ...addressQuestions(
        'previous_address',
        GROUP.previousAddress,
        PREVIOUS_ADDRESS_TARGETS,
        false,
        'If you’ve lived at your current address for less than 3 years, please provide your previous address below:',
    ),

    // CONTACT DETAILS
    {
        id: rep('mobile_number'),
        kind: 'short_text',
        label: 'Mobile number',
        textFormat: 'phone',
        group: GROUP.contact,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('home_number'),
        kind: 'short_text',
        label: 'Home number',
        textFormat: 'phone',
        group: GROUP.contact,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('business_number'),
        kind: 'short_text',
        label: 'Business number',
        textFormat: 'phone',
        group: GROUP.contact,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
    {
        id: rep('email_address'),
        kind: 'short_text',
        label: 'Email address',
        textFormat: 'email',
        required: true,
        group: GROUP.contact,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },

    // 3. REPRESENTATIVE - HEALTH. Each answer is special-category data under
    // Article 9, as in Part 6.
    {
        id: rep('general_health'),
        kind: 'single_select',
        label: 'How is your general state of health:',
        options: ['Excellent', 'Good', 'Fair', 'Poor'],
        group: GROUP.health,
        showWhen: APPOINTED,
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        id: rep('general_health_detail'),
        kind: 'long_text',
        label: "If 'Fair' or 'Poor', please provide further details",
        group: GROUP.health,
        showWhen: { questionId: rep('general_health'), equals: ['Fair', 'Poor'] },
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        id: rep('upcoming_operation'),
        kind: 'yes_no',
        label: 'Do you have any upcoming operation in the next 12 months, or are you currently recovering from one?',
        group: GROUP.health,
        showWhen: APPOINTED,
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        id: rep('upcoming_operation_detail'),
        kind: 'long_text',
        label: "If 'Yes', please provide further detail",
        group: GROUP.health,
        showWhen: { questionId: rep('upcoming_operation'), equals: ['Yes'] },
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        id: rep('other_health_conditions'),
        kind: 'yes_no',
        label: 'Do you have any other health conditions that we should be aware of that may impact your financial planning or risk profile?',
        group: GROUP.health,
        showWhen: APPOINTED,
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        id: rep('other_health_conditions_detail'),
        kind: 'long_text',
        label: 'If yes, please provide brief details if you are comfortable providing them to us. For example, chronic conditions or disabilities:',
        group: GROUP.health,
        showWhen: { questionId: rep('other_health_conditions'), equals: ['Yes'] },
        sensitive: true,
        excludeFromPrefill: true,
    },
    {
        // The Execution Only variant alone asks this question.
        id: rep('life_changes'),
        kind: 'single_select',
        label: 'Have there been any recent personal or family-related changes or events (such as bereavement, relationship issues, or major life transitions) to make us aware of?',
        options: [
            'No significant changes',
            'Minor life changes',
            'Single major life event',
            'Multiple major life events or ongoing crisis',
        ],
        group: GROUP.health,
        showWhen: APPOINTED_EXECUTION_ONLY,
        sensitive: true,
        excludeFromPrefill: true,
    },

    // 4. REPRESENTATIVE - KNOWLEDGE & EXPERIENCE. The Discretionary and
    // Advisory variant alone asks this section.
    {
        id: rep('portfolio_duration'),
        kind: 'single_select',
        label: 'How long, if at all, have you been investing?',
        options: ['None', 'under 1 yr', '1-3 yrs', '3-6 yrs', '7-9 yrs', '10+ yrs'],
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    {
        id: rep('knowledgeable_investor'),
        kind: 'single_select',
        label: 'Do you consider yourself a knowledgeable investor?',
        options: [
            'Strongly disagree',
            'Disagree',
            'Somewhat disagree',
            'Somewhat agree',
            'Agree',
            'Strongly agree',
        ],
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    ...PRODUCT_ROWS.map(
        (row, index): ClientApplicationQuestion => ({
            id: rep(`product_experience_${row.key}`),
            kind: 'single_select',
            label: row.label,
            ...(index === 0
                ? {
                      explainer: 'What experience do you have with the below investment products?',
                  }
                : {}),
            options: ['None', '1-3 yrs', '3+ yrs'],
            group: GROUP.knowledge,
            showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
            excludeFromPrefill: true,
        }),
    ),
    {
        id: rep('other_product_experience'),
        kind: 'long_text',
        label: 'Do you have experience with any other investment products? If so, please specify and provide the years of investment experience:',
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    {
        id: rep('invested_in_aim'),
        kind: 'yes_no',
        label: 'Have you ever invested in AIM before?',
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    {
        id: rep('has_financial_qualifications'),
        kind: 'yes_no',
        label: 'Do you have any financial qualifications?',
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    {
        id: rep('taken_part_in_placing_or_new_issue'),
        kind: 'yes_no',
        label: 'Have you ever taken part in a placing or a new issue (directly or indirectly through a fund or a portfolio)?',
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },
    {
        id: rep('worked_in_financial_industry'),
        kind: 'yes_no',
        label: 'Have you ever worked in the financial industry before?',
        group: GROUP.knowledge,
        showWhen: APPOINTED_DISCRETIONARY_OR_ADVISORY,
        excludeFromPrefill: true,
    },

    // 5. RISK TOLERANCE & CAPACITY FOR LOSS. The Discretionary and Advisory
    // variant alone asks this section. The representative answers for the
    // applicant, so the words name "the Applicant".
    riskQuestion(
        'protection_over_returns',
        'With regards to this investment, the Applicant generally considers protecting their investment is more important than the potential to achieve high returns:',
        AGREEMENT_SCALE,
        "Whether it’s your first investment or you're an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions about your financial future. The questions in this section help us evaluate your tolerance and capacity for risk, ensuring we apply the appropriate risk profile to your portfolio. You can find out more in our Risk and Investment guide and Capacity for Loss guide. Responses are to be completed by the Nominated or Legal Representative on behalf of the Applicant, the Applicant's signature will confirm their agreement for us to assess the risk profile of the Applicant portfolio using the answers provided here.",
    ),
    riskQuestion(
        'prefer_fewer_ups_and_downs',
        'With regards to this investment, the Applicant would prefer fewer ups and downs in value, and they would be willing to accept the lower returns this investment may make:',
        AGREEMENT_SCALE,
    ),
    riskQuestion(
        'comfortable_with_smaller_companies',
        'Smaller companies can achieve faster and higher growth but can also be illiquid and at greater risk of failure than FTSE 100 companies. The Applicant would be comfortable to invest in this type of company for the potential to achieve a larger return:',
        AGREEMENT_SCALE,
    ),
    riskQuestion(
        'understands_smaller_company_risks',
        'The Applicant understands – and is comfortable with – the risks associated with investing in smaller companies:',
        AGREEMENT_SCALE,
    ),
    riskQuestion(
        'response_to_quarter_fall',
        'In the four weeks before the first lockdown (23 March 2020), the FTSE 100 fell 25% in value. If the Applicant owned an investment that lost over 25% in four weeks, they would:',
        [
            'Sell all the remaining investment',
            'Sell some of the remaining investment',
            'Hold onto the investment and sell nothing',
            'Buy more of the investment',
        ],
    ),
    riskQuestion(
        'loss_that_prompts_move_to_cash',
        'Over the short term, investments are likely to fall as well as rise. The Applicant would begin to feel the need to cut their losses and move to cash upon a loss of:',
        [
            'Any fall is cause for concern',
            'Loss of 15%',
            'Loss of 16-30%',
            'Loss of 31-49%',
            'Loss of 50%+',
        ],
    ),
    riskQuestion(
        'investment_horizon',
        'How long do you think the Applicant will remain invested before taking their money out?',
        ['0-1 years', '1-3 years', '3-5 years', '5-7 years', '7+ years'],
    ),
    riskQuestion(
        'income_security',
        // The paper form prints "Appliant's". The typo is corrected here.
        "How secure are the Applicant's sources of income?",
        ['Very unsecure', 'Unsecure', 'Not sure', 'Secure', 'Very secure'],
    ),
    riskQuestion(
        'income_change_over_ten_years',
        "Over the course of the next ten years, how do you expect the Applicant's level of income to change, if at all?",
        TEN_YEAR_CHANGE_SCALE,
    ),
    riskQuestion(
        'liquid_funds_months_of_expenses',
        "How many months of expenses could the Applicant's liquid funds cover?",
        ['No emergency funds', 'Less than a month', '1-6 months', '6-18 months', 'Over 18 months'],
    ),
    riskQuestion(
        'capacity_for_loss',
        "How much of the Applicant's investments with Dowgate Wealth do you think that they are able to lose without having a significant impact on their future standard of living?",
        [
            'No losses (0-10%)',
            'Small losses (11-20%)',
            'Moderate losses (21-49%)',
            'Large losses (50-90%)',
            'Total losses (91-100%)',
        ],
    ),
    riskQuestion(
        'liabilities_change_over_ten_years',
        "Over the course of the next ten years, how do you expect the Applicant's liabilities to change, if at all?",
        TEN_YEAR_CHANGE_SCALE,
    ),

    // SIGNATURES. The client accepts the four declarations with a tick. The
    // representative cannot sign online, so the firm gets that signature
    // off-line, and the explainer says so.
    {
        id: rep('declarations_accepted'),
        kind: 'acknowledgement',
        label: 'I accept the declarations above',
        explainer:
            'Your representative must also sign this appointment. We will contact you to arrange the signature of your representative.',
        statements: [
            {
                id: 'nominated_representative_declaration_1',
                text: 'I/We act in my/our name(s) as specified on this form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are my/our own funds.',
            },
            {
                id: 'nominated_representative_declaration_2',
                text: 'I/We am/are not aware of any health or other conditions that I/we may have that might affect the proper operation of the account.',
            },
            {
                id: 'nominated_representative_declaration_3',
                text: 'I/We have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
            },
            {
                id: 'nominated_representative_declaration_4',
                text: 'I/We have read and understood the definition and relevant consequences of the appointment of the specified representative on this form.',
            },
        ],
        required: true,
        group: GROUP.signatures,
        showWhen: APPOINTED,
        excludeFromPrefill: true,
    },
];

/**
 * The questions of the representative block, in the order of the paper form.
 * The first one is the yes or no question that opens the others. Form
 * version 3.0 adds each one.
 */
export const NOMINATED_REPRESENTATIVE_QUESTIONS: readonly ClientApplicationQuestion[] =
    QUESTIONS.map((question) => ({ ...question, introducedIn: '3.0' }));

/** Each question id of the representative block, the yes or no question first. */
export const NOMINATED_REPRESENTATIVE_QUESTION_IDS: readonly string[] =
    NOMINATED_REPRESENTATIVE_QUESTIONS.map((question) => question.id);

/** The question ids of the knowledge and the risk sections, which only the Discretionary and Advisory variant asks. */
export const NOMINATED_REPRESENTATIVE_DISCRETIONARY_AND_ADVISORY_IDS: readonly string[] =
    NOMINATED_REPRESENTATIVE_QUESTIONS.filter(
        (question) => question.group === GROUP.knowledge || question.group === GROUP.risk,
    ).map((question) => question.id);

/** The question ids that only the Execution Only variant asks. */
export const NOMINATED_REPRESENTATIVE_EXECUTION_ONLY_IDS: readonly string[] = [rep('life_changes')];
